import React from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import { useForcedNav } from './hooks/use-forced-nav';
import { navLayout } from './nav-layout';
import { GlobalNavLinkContentProps, useGlobalNavContext } from './nav-links-container';

export type GlobalNavLinkProps = GlobalNavLinkContentProps & {
	isLinkVisible?: boolean;
	isGlobalNav?: boolean;
};

const globalNavLinkSpacing = css({
	paddingInlineStart: navLayout.menuItemPadding,
	paddingInlineEnd: navLayout.menuItemPadding,
});

const buildAnchorLinkClassName = (
	navLinkIsCurrentPage: boolean,
	isCurrentOpenDropdown: boolean,
	hasDropdown: boolean,
	isActive?: boolean,
): string => {
	return [
		'glob-nav-anchor-link',
		'nav-link-anchor',
		navLinkIsCurrentPage && 'isCurrentPage',
		((isCurrentOpenDropdown && hasDropdown) || isActive) && 'isActive',
		!hasDropdown && 'isHoverableLink',
	]
		.filter(Boolean)
		.join(' ');
};

const normalizePathname = (path: string) => {
	const pathname = (path.split(/[?#]/, 1)[0] ?? '').replace(/\/+$/, '');
	return pathname || '/';
};

const hrefPathname = (href: string) => {
	try {
		const url = new URL(href, globalThis.location.origin);
		return url.origin === globalThis.location.origin ? url.pathname : href;
	} catch {
		return href;
	}
};

export const isPathWithinHref = (pathname: string, href: string) => {
	const normalizedPathname = normalizePathname(pathname);
	const normalizedHref = normalizePathname(hrefPathname(href));

	return (
		normalizedPathname === normalizedHref ||
		(normalizedHref !== '/' && normalizedPathname.startsWith(`${normalizedHref}/`))
	);
};

const getDropdownContentHrefs = (content: GlobalNavLinkContentProps['content']) => {
	if (
		!React.isValidElement<{
			contentSections?: Array<{ links?: Array<{ href?: string }> }>;
		}>(content)
	) {
		return [];
	}

	return (content.props.contentSections ?? []).flatMap((section) =>
		(section.links ?? []).flatMap((link) => (link.href ? [link.href] : [])),
	);
};

const getActiveHrefs = (props: GlobalNavLinkProps) => {
	if (props.activeHrefs?.length) {
		return props.activeHrefs;
	}

	return getDropdownContentHrefs(props.content);
};

// eslint-disable-next-line complexity
const GlobalNavLink = (props: GlobalNavLinkProps) => {
	// Using useGlobalNavContext hook which uses context, so component must memoize
	// to prevent unnecessary re-renders when context changes but props don't
	const { current, setCurrent, setCurrentByClick, isClickLocked } = useGlobalNavContext();
	const { isForcingNav, forcedNavKey } = useForcedNav();

	const isHover = !checkFeatureFlagGate(featureFlags.gwpGlobalNavHoverVClick);

	const isCurrentOpenDropdown = current === props.globalLinksKey;
	const directLinkIsCurrentPage =
		props.href && normalizePathname(globalThis.location.pathname) === normalizePathname(props.href);
	const activeHrefs = getActiveHrefs(props);
	const nestedLinkIsCurrentPage = activeHrefs.some((href) =>
		isPathWithinHref(globalThis.location.pathname, href),
	);
	const navLinkIsCurrentPage = directLinkIsCurrentPage || nestedLinkIsCurrentPage;

	const anchorLinkClassName = buildAnchorLinkClassName(
		!!navLinkIsCurrentPage,
		isCurrentOpenDropdown,
		!!props.hasDropdown,
		!!props.isActive,
	);

	const { isDarkTheme } = useTheme();

	const linkClickAction: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
		if (props.hasDropdown) {
			e.preventDefault();

			if (isForcingNav && forcedNavKey !== props.globalLinksKey) {
				return;
			}

			// Use isCurrentOpenDropdown (from context) as the source of truth
			// rather than local isOpen state, which can drift out of sync when
			// the user hovers between items or the context is updated externally.
			if (isCurrentOpenDropdown && isClickLocked) {
				// Already click-locked open → second click closes it
				setCurrentByClick('');
			} else if (isCurrentOpenDropdown && !isClickLocked) {
				// Opened by hover, first click → keep it open and click-lock it
				setCurrentByClick(props.globalLinksKey);
			} else {
				// Not open → open it via click (click-locks it)
				setCurrentByClick(props.globalLinksKey);
			}
		}
	};

	const rotate180deg = css({
		transform: 'rotate(180deg)',
	});

	const darkThemeLinkStyles = css({
		color: wacToken('color.neutral.light0'),
		['&:hover']: {
			color: wacToken('color.extended.blue300'),
		},
		['&.isActive, &.isCurrentPage, &.isActive:hover, &.isCurrentPage:hover']: {
			color: wacToken('color.extended.blue300'),
		},
	});

	return (
		<a
			className={anchorLinkClassName}
			style={{
				pointerEvents: props.isLinkVisible === false ? 'none' : 'auto',
			}}
			css={[
				css({
					paddingBlockStart: `var(--nav-menu-padding-block-start, ${token('space.100')})`,
					display: 'flex',
					alignItems: 'center',
					height: '100%',
					textDecoration: 'none',
					lineHeight: '1rem',

					// if padding val needs to change, update calculateMaxChildren function (search for jfind-1024)

					paddingInlineStart: token('space.100'),
					paddingInlineEnd: token('space.300'),
					['&.isActive']: {
						color: wacToken('color.extended.blue800'),
					},
					['&:focus-visible']: {
						outlineOffset: '-2px',
					},
					['&:hover']: {
						color: wacToken('color.extended.blue800'),
					},
					[media.above.md]: {
						borderRadius: '3px',
						textDecoration: 'none',
					},
				}),
				props.isGlobalNav && globalNavLinkSpacing,
				isDarkTheme && darkThemeLinkStyles,
			]}
			href={props.href ?? '#'}
			onClick={linkClickAction}
			onMouseEnter={
				isHover
					? () => {
							if (isForcingNav && forcedNavKey !== props.globalLinksKey) {
								return;
							}

							// When a dropdown is click-locked open, hovering other
							// links should not switch the active dropdown.
							if (isClickLocked && !isCurrentOpenDropdown) {
								return;
							}

							setCurrent(props.globalLinksKey);
						}
					: undefined
			}
			onFocus={
				() => {
					return;
				} /* clicks are supported, using empty function for ratcheted @atlassian/a11y/mouse-events-have-key-events lint rule */
			}
			aria-label={props.linkText}
			aria-expanded={props.hasDropdown ? !!isCurrentOpenDropdown : undefined}
			aria-current={directLinkIsCurrentPage ? 'page' : undefined}
			role="menuitem"
			aria-haspopup={props.hasDropdown ? 'true' : undefined}
			data-event="clicked"
			data-event-component="linkButton"
			data-event-container="headerDropdownLink"
			data-label={props.linkText}
			data-product-key={props.productKey ?? 'jiraSoftware'}
		>
			{props.linkText}
			{!!props.hasDropdown && (
				<div
					className="chevron-icon"
					style={{
						opacity: props.isLinkVisible === false ? 0 : 1,
					}}
					css={[
						css({
							display: 'block',
							marginInlineStart: '8px',
							lineHeight: 0,
							transition: '250ms',
						}),
						isCurrentOpenDropdown && rotate180deg,
					]}
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="11"
						height="6"
						viewBox="0 0 11 6"
						fill="none"
						aria-hidden="true"
						focusable="false"
					>
						<path
							d="M9.19922 1.18176L5.19922 4.81813L1.19922 1.18176"
							stroke={
								isDarkTheme
									? 'currentColor'
									: isCurrentOpenDropdown
										? wacToken('color.extended.blue800')
										: wacToken('color.primary.black')
							}
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</div>
			)}
		</a>
	);
};

// Memoize to prevent unnecessary re-renders when parent component updates
// but props haven't changed. This is important because GlobalNavLink consumes
// GlobalNavContext which updates frequently (when dropdowns open/close),
// and we only want to re-render this link if its props actually change.
const MemoizedGlobalNavLink = React.memo(GlobalNavLink);
export default MemoizedGlobalNavLink;
